import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AccessGate from '../components/AccessGate';
import HtmlWithDataTables from '../components/DataTable';
import PageWidgets from '../components/PageWidgets';

export default function DynamicPage() {
  const params = useParams();
  const slug = params.slug || params.productId;
  const navigate = useNavigate();
  const { customPages, loaded, isAdmin, canAccess, canEditPage, editPageContent } = useAuth();
  const page = customPages.find((p) => p.page_key === slug);
  const [editing, setEditing] = useState(null); // draft content while editing inline
  const [busy, setBusy] = useState(false);

  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center fade-in">
        <h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>
          {loaded ? 'Page not found' : 'Loading\u2026'}
        </h1>
      </div>
    );
  }

  const canEdit = canEditPage(page);
  const isPageAdmin = isAdmin || canAccess('admin-pages');

  if (page.draft && !(page.publish_at && new Date(page.publish_at).getTime() <= Date.now()) && !canEdit) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center fade-in" data-testid="draft-notice">
        <h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>Coming soon</h1>
        <p className="mt-2" style={{ color: 'var(--olive)' }}>This page isn’t published yet. Please check back shortly.</p>
      </div>
    );
  }

  const paragraphs = (page.body || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  const openEditor = () => {
    if (isPageAdmin) { navigate(`/admin/pages?edit=${page.page_key}`); return; }
    setEditing({ title: page.title || '', intro: page.intro || '', body: page.body || '', html: page.html || '' });
  };

  const saveInline = async () => {
    setBusy(true);
    try {
      const payload = page.html
        ? { title: editing.title, html: editing.html }
        : { title: editing.title, intro: editing.intro, body: editing.body };
      await editPageContent(page.page_key, payload);
      toast.success('Page saved.');
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the page.');
    } finally { setBusy(false); }
  };

  return (
    <AccessGate pageKey={page.page_key}>
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>{page.title}</h1>
          {canEdit && (
            <button type="button" data-testid={`edit-page-icon-${page.page_key}`} title="Edit this page" onClick={openEditor}
              className="inline-flex items-center gap-1 align-middle px-2 py-1 rounded text-xs font-title transition-opacity hover:opacity-90"
              style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
              <Pencil size={13} /> Edit
            </button>
          )}
        </div>
        <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
        {page.html ? (
          <HtmlWithDataTables html={page.html} />
        ) : (<>
          {page.intro && <p className="text-[15px] leading-relaxed mb-5 font-medium">{page.intro}</p>}
          {paragraphs.map((p, i) => (
            <p key={i} className="text-[15px] leading-relaxed mb-4" style={{ whiteSpace: 'pre-line' }}>{p}</p>
          ))}
          {paragraphs.length === 0 && !page.intro && !(page.widgets && page.widgets.length) && (
            <p className="text-[15px]" style={{ color: 'var(--olive)' }}>This page has no content yet.</p>
          )}
        </>)}
        <PageWidgets widgets={page.widgets} />
      </div>

      {editing && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} data-testid="page-inline-editor">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Edit page</h3>
              <button onClick={() => setEditing(null)} data-testid="page-inline-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Title</label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="page-inline-title" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
              {page.html ? (
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Content (HTML)</label>
                  <textarea value={editing.html} onChange={(e) => setEditing({ ...editing, html: e.target.value })} rows={12} data-testid="page-inline-html" className="w-full px-2 py-1.5 rounded border text-sm font-mono" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
              ) : (<>
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Intro</label>
                  <textarea value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} rows={2} data-testid="page-inline-intro" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Body</label>
                  <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={10} data-testid="page-inline-body" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
              </>)}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={saveInline} disabled={busy} data-testid="page-inline-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </AccessGate>
  );
}
